/**
 * AxiomStatusChip — compact header chip on the Connectors page that
 * reflects engine reachability as a proxy for AXIOM federation
 * connectivity.
 *
 * Phase 5 policy: Connectors CRUD stays on the mock layer (page
 * still functions offline). This chip probes `/health` via the
 * shared `useEngineHealth` hook and surfaces one of four states:
 *
 *   - offline     → engine unreachable (mock-mode dev, or server
 *                   process stopped)
 *   - probing     → initial fetch in flight
 *   - healthy     → engine healthy; read as "Federation up"
 *   - degraded    → engine up but at least one check degraded
 *   - unhealthy   → at least one unhealthy check
 *
 * When Window 1 adds a federation-specific `HealthCheck`, the chip
 * lifts its label from that outcome instead of the aggregate.
 */

import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';
import { useEngineHealth } from '@/hooks/useCommand';

type State = 'offline' | 'probing' | 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

export function AxiomStatusChip() {
  const { t } = useTranslation(['connectors', 'command']);
  const health = useEngineHealth();

  const state: State =
    health.data === null
      ? 'offline'
      : health.data === undefined
        ? 'probing'
        : health.data.status;

  const { cls, dot } = chipClasses(state);

  return (
    <span
      role="status"
      aria-label={t('axiom.aria_label')}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium',
        cls,
      )}
    >
      <Dot className={dot} />
      {t('axiom.label')}
      <span aria-hidden="true" className="text-ink-subtle">·</span>
      <span>{t(`axiom.state.${state}`)}</span>
    </span>
  );
}

function Dot({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', className)} />
  );
}

function chipClasses(state: State): { cls: string; dot: string } {
  switch (state) {
    case 'healthy':
      return {
        cls: 'border-success/30 bg-success-soft text-success',
        dot: 'bg-success',
      };
    case 'degraded':
      return {
        cls: 'border-warn/30 bg-warn-soft text-warn',
        dot: 'bg-warn',
      };
    case 'unhealthy':
      return {
        cls: 'border-danger/30 bg-danger/10 text-danger',
        dot: 'bg-danger',
      };
    case 'probing':
      return {
        cls: 'border-border bg-surface-raised text-ink-muted',
        dot: 'bg-ink-muted animate-pulse',
      };
    case 'offline':
    case 'unknown':
    default:
      return {
        cls: 'border-border bg-surface-raised text-ink-subtle',
        dot: 'bg-ink-subtle',
      };
  }
}
